import type { BotDecision } from '../controllers/BotController.ts';
import { Backdrop, COLORS, GROUND_ART_HEIGHT } from './Backdrop.ts';
import type { GameConfig, GameState, PipeSnapshot } from './GameState.ts';
import { ART_SCALE, bake, birdFrame } from './Sprites.ts';

/**
 * `arcade` -- 8-bit pixel art, rendered at native low resolution and upscaled
 *   with nearest-neighbour filtering.
 * `lab` -- flat geometry on a dark field. Higher contrast, no scenery competing
 *   with the controller overlay. Use it for figures and screenshots where the
 *   aim point and the prediction have to be unambiguous.
 */
export type RenderTheme = 'arcade' | 'lab';

export interface RenderOverlay {
  /** The bot's latest decision, if a bot is in the loop. */
  decision?: BotDecision | null;
  /** Stimulation requests issued and not yet answered. */
  openRequests?: number;
  banner?: string | null;
  subBanner?: string | null;
  fps?: number;
  /** Dim everything and label it; used by replay. */
  replayLabel?: string | null;
  showBotVision: boolean;
}

const LAB = {
  sky: '#10141c',
  skyBand: '#161c27',
  ground: '#1d2430',
  pipe: '#3ba55d',
  pipeEdge: '#2d8049',
  bird: '#f5c542',
  birdDead: '#8a7431',
};

const OVERLAY = {
  text: '#e6e9ef',
  dim: '#9aa4b8',
  target: '#4aa3ff',
  gapCenter: '#2f6fae',
  predicted: '#ff4d8d',
  path: 'rgba(255,77,141,0.65)',
  stim: '#ff9f43',
  halo: 'rgba(8,10,14,0.85)',
};

/**
 * Canvas renderer.
 *
 * Two things are going on at once and they are kept strictly apart:
 *
 *   1. **The game**, drawn into an offscreen buffer at one pixel per art pixel
 *      and blitted up by an integer factor with smoothing off. Nothing is drawn
 *      at a fractional position, which is what makes it look 8-bit rather than
 *      blurry.
 *   2. **The instrument overlay** -- aim point, latency-compensated prediction,
 *      forecast path, reason string, in-flight stimulation requests -- drawn
 *      afterwards at full device resolution, so the text stays sharp and the
 *      lines stay thin. That layer is the reason this project exists and it
 *      must not be chunky.
 *
 * The renderer is a pure function of (state, overlay). It holds no game state
 * and mutates nothing, so it can be pointed at a replay's reconstructed state
 * exactly as easily as at the live one.
 */
export class Renderer {
  theme: RenderTheme = 'arcade';

  private ctx: CanvasRenderingContext2D;
  private art: HTMLCanvasElement;
  private artCtx: CanvasRenderingContext2D;
  private backdrop: Backdrop;

  /** Size of the art buffer, in art pixels. */
  private readonly artWidth: number;
  private readonly artHeight: number;
  /** Height of the playable field in art pixels; the death line. */
  private readonly fieldArtHeight: number;

  private lastFlapMs = -1e9;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly config: GameConfig,
  ) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;

    this.artWidth = Math.round(config.width / ART_SCALE);
    this.fieldArtHeight = Math.round(config.height / ART_SCALE);
    this.artHeight = this.fieldArtHeight + GROUND_ART_HEIGHT;

    this.art = document.createElement('canvas');
    this.art.width = this.artWidth;
    this.art.height = this.artHeight;
    const artCtx = this.art.getContext('2d');
    if (!artCtx) throw new Error('2D context unavailable for the art buffer');
    this.artCtx = artCtx;
    this.artCtx.imageSmoothingEnabled = false;

    this.backdrop = new Backdrop(this.artWidth, this.fieldArtHeight);

    // The element's aspect ratio has to include the ground strip, or the
    // backing store and the CSS box disagree and the art stops being square.
    canvas.style.aspectRatio = `${this.artWidth} / ${this.artHeight}`;
    this.resize();
  }

  /** Total logical height including the ground strip below the death line. */
  get logicalHeight(): number {
    return this.artHeight * ART_SCALE;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  /** Called when a flap is executed, to trigger the wing animation. */
  noteFlap(nowMs: number): void {
    this.lastFlapMs = nowMs;
  }

  render(state: GameState, overlay: RenderOverlay, nowMs: number): void {
    const { ctx } = this;
    const cw = this.canvas.width;
    const ch = this.canvas.height;

    // Integer upscale wherever there is room for one. Below 2x there is not
    // enough resolution to be picky and a fractional factor beats letterboxing
    // away half the window.
    const raw = Math.min(cw / this.artWidth, ch / this.artHeight);
    const k = raw >= 2 ? Math.floor(raw) : raw;
    const ox = Math.round((cw - this.artWidth * k) / 2);
    const oy = Math.round((ch - this.artHeight * k) / 2);

    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = this.theme === 'arcade' ? '#1b2733' : '#0b0e13';
    ctx.fillRect(0, 0, cw, ch);

    if (this.theme === 'arcade') {
      this.drawArtScene(state, nowMs);
      ctx.drawImage(
        this.art, 0, 0, this.artWidth, this.artHeight,
        ox, oy, this.artWidth * k, this.artHeight * k,
      );
    }

    // Overlay and HUD work in logical game coordinates at full device
    // resolution: `k / ART_SCALE` device pixels per logical pixel.
    ctx.save();
    ctx.translate(ox, oy);
    ctx.scale(k / ART_SCALE, k / ART_SCALE);
    ctx.imageSmoothingEnabled = true;

    if (this.theme === 'lab') this.drawLabScene(state);
    if (overlay.showBotVision && overlay.decision) this.drawBotOverlay(state, overlay.decision);
    this.drawHud(state, overlay);

    ctx.restore();
  }

  // ------------------------------------------------------------- 8-bit scene

  private drawArtScene(state: GameState, nowMs: number): void {
    const ctx = this.artCtx;
    const speedArt = state.pipeSpeed / ART_SCALE;

    this.backdrop.drawSky(ctx, this.fieldArtHeight);
    this.backdrop.drawClouds(ctx, state.simTimeMs, speedArt);
    this.backdrop.drawBushes(ctx, state.simTimeMs, speedArt, this.fieldArtHeight);

    for (const pipe of state.pipes) this.drawArtPipe(ctx, pipe);

    this.backdrop.drawGround(ctx, state.simTimeMs, speedArt, this.fieldArtHeight);
    this.drawArtBird(ctx, state, nowMs);
  }

  /**
   * A pipe: shaft plus a wider collar at the gap end.
   *
   * The collar overhangs the shaft by 2 art pixels on each side and is drawn
   * *outside* the collision rectangle, so the visible lip is slightly wider
   * than the thing that kills you. That matches the original, and it is the
   * forgiving direction to be wrong in.
   */
  private drawArtPipe(ctx: CanvasRenderingContext2D, pipe: PipeSnapshot): void {
    const x = Math.round(pipe.x / ART_SCALE);
    const w = Math.round(this.config.pipeWidth / ART_SCALE);
    const gapTop = Math.round(pipe.gapTop / ART_SCALE);
    const gapBottom = Math.round(pipe.gapBottom / ART_SCALE);
    const collarH = 12;
    const collarOverhang = 2;

    const shaft = (y: number, h: number) => {
      if (h <= 0) return;
      ctx.fillStyle = COLORS.pipeOutline;
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = COLORS.pipe;
      ctx.fillRect(x + 1, y, w - 2, h);
      ctx.fillStyle = COLORS.pipeLight;
      ctx.fillRect(x + 3, y, 3, h);
      ctx.fillStyle = COLORS.pipeDark;
      ctx.fillRect(x + w - 5, y, 3, h);
    };

    const collar = (y: number) => {
      const cx = x - collarOverhang;
      const cw = w + collarOverhang * 2;
      ctx.fillStyle = COLORS.pipeOutline;
      ctx.fillRect(cx, y, cw, collarH);
      ctx.fillStyle = COLORS.pipe;
      ctx.fillRect(cx + 1, y + 1, cw - 2, collarH - 2);
      ctx.fillStyle = COLORS.pipeLight;
      ctx.fillRect(cx + 3, y + 1, 3, collarH - 2);
      ctx.fillStyle = COLORS.pipeDark;
      ctx.fillRect(cx + cw - 5, y + 1, 3, collarH - 2);
    };

    shaft(0, gapTop - collarH);
    collar(gapTop - collarH);
    collar(gapBottom);
    shaft(gapBottom + collarH, this.fieldArtHeight - gapBottom - collarH);
  }

  /**
   * The bird, tilted by vertical velocity.
   *
   * The tilt is cosmetic: collision is a circle on the body centre and does not
   * rotate. Rotation is quantised to 15-degree steps and drawn with smoothing
   * off, so the sprite stays jaggy the way rotated 8-bit art should.
   */
  private drawArtBird(ctx: CanvasRenderingContext2D, state: GameState, nowMs: number): void {
    const frame = birdFrame(nowMs, this.lastFlapMs, state.alive);
    const img = bake(frame);
    const cx = Math.round(state.birdX / ART_SCALE);
    const cy = Math.round(state.birdY / ART_SCALE);

    // -25 degrees climbing through +85 degrees in a full dive.
    const t = Math.max(-1, Math.min(1, state.birdVelocityY / this.config.maxFallSpeed));
    const degrees = state.alive ? (t < 0 ? t * 25 : t * 85) : 90;
    const step = 15;
    const snapped = (Math.round(degrees / step) * step * Math.PI) / 180;

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(cx, cy);
    ctx.rotate(snapped);
    ctx.drawImage(img, -Math.round(frame.width / 2), -Math.round(frame.height / 2));
    ctx.restore();
  }

  // --------------------------------------------------------------- lab scene

  private drawLabScene(state: GameState): void {
    const ctx = this.ctx;
    const { width, height, pipeWidth, birdX, birdRadius } = this.config;

    ctx.fillStyle = LAB.sky;
    ctx.fillRect(0, 0, width, this.logicalHeight);
    ctx.fillStyle = LAB.skyBand;
    for (let y = 0; y < height; y += 80) ctx.fillRect(0, y, width, 40);
    ctx.fillStyle = LAB.ground;
    ctx.fillRect(0, height, width, this.logicalHeight - height);

    for (const pipe of state.pipes) {
      ctx.fillStyle = LAB.pipe;
      ctx.fillRect(pipe.x, 0, pipeWidth, pipe.gapTop);
      ctx.fillRect(pipe.x, pipe.gapBottom, pipeWidth, height - pipe.gapBottom);
      ctx.fillStyle = LAB.pipeEdge;
      ctx.fillRect(pipe.x, pipe.gapTop - 8, pipeWidth, 8);
      ctx.fillRect(pipe.x, pipe.gapBottom, pipeWidth, 8);
    }

    ctx.fillStyle = state.alive ? LAB.bird : LAB.birdDead;
    ctx.beginPath();
    ctx.arc(birdX, state.birdY, birdRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = ctx.fillStyle;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(birdX, state.birdY);
    ctx.lineTo(birdX, state.birdY + state.birdVelocityY * 0.06);
    ctx.stroke();
  }

  // -------------------------------------------------------------- bot vision

  /**
   * What the controller currently sees and why it decided to flap.
   *
   * Five things, in order of importance:
   *   - the aim point (blue solid) -- where the bot is steering
   *   - the gap centre (blue dotted) -- what the aim point is derived from
   *   - the latency-compensated prediction (pink dot) -- where the bot believes
   *     the bird will be when a request issued now finally lands
   *   - the forecast path (pink line) -- the same prediction over the next
   *     second, including impulses already in flight through the actuator
   *   - the error bar between prediction and aim point, which is literally the
   *     quantity the decision rule thresholds
   *
   * Every stroke is drawn twice, dark underneath and coloured on top. Against
   * a bright 8-bit sky a bare 1 px line is invisible, and an overlay you cannot
   * read is worse than no overlay.
   */
  private drawBotOverlay(state: GameState, d: BotDecision): void {
    const ctx = this.ctx;
    const { width, birdX, height } = this.config;

    // Clip to the play field. The forecast is ballistic and happily continues
    // below the death line; drawing it across the ground implies the bird could
    // go there.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    ctx.clip();

    // The true hitbox. The sprite is deliberately larger than it, and
    // `hitboxScale` may shrink it further, so without this a near miss on
    // screen is not a near miss in the simulation.
    ctx.strokeStyle = 'rgba(255,255,255,0.42)';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.arc(state.birdX, state.birdY, state.collisionRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.setLineDash([4, 6]);
    this.haloLine(0, d.gapCenterY, width, d.gapCenterY, OVERLAY.gapCenter, 1.5);
    ctx.setLineDash([]);
    this.haloLine(0, d.targetY, width, d.targetY, OVERLAY.target, 2);

    if (d.predictedPath.length > 1) {
      const path = new Path2D();
      for (let i = 0; i < d.predictedPath.length; i++) {
        const p = d.predictedPath[i];
        const x = birdX + p.t * state.pipeSpeed;
        if (i === 0) path.moveTo(x, p.y);
        else path.lineTo(x, p.y);
      }
      ctx.lineJoin = 'round';
      ctx.strokeStyle = OVERLAY.halo;
      ctx.lineWidth = 4;
      ctx.stroke(path);
      ctx.strokeStyle = OVERLAY.path;
      ctx.lineWidth = 2;
      ctx.stroke(path);
    }

    const predX = birdX + (d.horizonMs / 1000) * state.pipeSpeed;
    ctx.setLineDash([3, 3]);
    this.haloLine(predX, d.predictedY, predX, d.targetY, OVERLAY.predicted, 1.5);
    ctx.setLineDash([]);

    ctx.fillStyle = OVERLAY.halo;
    ctx.beginPath();
    ctx.arc(predX, d.predictedY, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = OVERLAY.predicted;
    ctx.beginPath();
    ctx.arc(predX, d.predictedY, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.font = '600 11px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textAlign = 'left';
    this.haloText(`aim ${d.targetY.toFixed(0)}`, 6, d.targetY - 5, OVERLAY.target);
    // With no compensation the prediction sits on the bird, so the label is
    // pushed clear of the sprite rather than written across it.
    const labelX = predX + (d.horizonMs > 0 ? 10 : 30);
    const labelY = d.predictedY - (d.horizonMs > 0 ? 9 : 24);
    this.haloText(
      `+${d.horizonMs.toFixed(0)}ms → ${d.predictedY.toFixed(0)} (err ${d.errorY >= 0 ? '+' : ''}${d.errorY.toFixed(0)})`,
      labelX,
      labelY,
      OVERLAY.predicted,
    );

    ctx.restore();
  }

  // --------------------------------------------------------------------- hud

  private drawHud(state: GameState, overlay: RenderOverlay): void {
    const ctx = this.ctx;
    const { width, height } = this.config;
    const total = this.logicalHeight;

    // Score, arcade-style: big, centred, heavily outlined.
    ctx.textAlign = 'center';
    ctx.font = '900 42px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#20201a';
    ctx.lineWidth = 7;
    ctx.strokeText(String(state.score), width / 2, 62);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(String(state.score), width / 2, 62);

    // Instrument readouts sit in a chip so they survive any backdrop.
    ctx.textAlign = 'left';
    ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace';
    const chip = [`tick ${state.tick}`, `${(state.simTimeMs / 1000).toFixed(1)}s`];
    if (overlay.fps !== undefined) chip.push(`${overlay.fps.toFixed(0)} fps`);
    const label = chip.join('  ');
    const w = ctx.measureText(label).width;
    ctx.fillStyle = OVERLAY.halo;
    ctx.fillRect(6, 8, w + 12, 18);
    ctx.fillStyle = OVERLAY.dim;
    ctx.fillText(label, 12, 21);

    // In-flight stimulation requests: the visible difference between what the
    // bot asked for and what the bird has actually been given.
    if (overlay.openRequests && overlay.openRequests > 0) {
      const n = Math.min(overlay.openRequests, 8);
      ctx.fillStyle = OVERLAY.halo;
      ctx.fillRect(width - 18 - n * 10, 8, n * 10 + 12, 18);
      ctx.fillStyle = OVERLAY.stim;
      for (let i = 0; i < n; i++) ctx.fillRect(width - 16 - i * 10, 12, 6, 10);
      ctx.textAlign = 'right';
      ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';
      this.haloText('awaiting response', width - 8, 40, OVERLAY.stim);
      ctx.textAlign = 'left';
    }

    if (overlay.decision) {
      ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace';
      this.haloText(
        overlay.decision.reason,
        10,
        total - 10,
        overlay.decision.flap ? '#ffe27a' : OVERLAY.dim,
      );
    }

    if (overlay.replayLabel) {
      ctx.textAlign = 'right';
      ctx.font = '600 11px ui-monospace, SFMono-Regular, Menlo, monospace';
      this.haloText(overlay.replayLabel, width - 10, total - 10, '#7cc4ff');
      ctx.textAlign = 'left';
    }

    if (overlay.banner) {
      ctx.fillStyle = 'rgba(11,14,19,0.76)';
      ctx.fillRect(0, height / 2 - 56, width, 112);
      ctx.textAlign = 'center';
      ctx.font = '900 26px ui-monospace, SFMono-Regular, Menlo, monospace';
      this.haloText(overlay.banner, width / 2, height / 2 - 4, '#ffffff');
      if (overlay.subBanner) {
        ctx.font = '12px ui-monospace, SFMono-Regular, Menlo, monospace';
        this.haloText(overlay.subBanner, width / 2, height / 2 + 22, OVERLAY.dim);
      }
      ctx.textAlign = 'left';
    }
  }

  // ------------------------------------------------------------------ helpers

  private haloLine(
    x1: number, y1: number, x2: number, y2: number, color: string, lineWidth: number,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = OVERLAY.halo;
    ctx.lineWidth = lineWidth + 2.5;
    ctx.stroke();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }

  private haloText(text: string, x: number, y: number, color: string): void {
    const ctx = this.ctx;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = OVERLAY.halo;
    ctx.lineWidth = 3.5;
    ctx.strokeText(text, x, y);
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  }
}
